/* assets/littleg-share.js — MAPBOX ASSEMBLY SHARE ENGINE
 * Universal, scenario-driven runtime for Web Share API & Link Copying.
 * Features:
 * - Direct integration with <share-button> Web Components & static share cards.
 * - Instant tactile feedback (icon toggles & localized status pills).
 * - Public city return URL assembly without local storage coupling.
 */
(function () {
  'use strict';

  // Legacy scene identifiers are stable. Only public Collection destinations change.
  var PUBLIC_HANDLE_FALLBACKS = {
    'digital-city': 'digital-city-geospatial-analytics',
    'green-city-service': 'ecology-environmental-assessment',
    'infrastructure-engineering': 'surveying-geomatics',
    'industry-park-development': 'industrial-park-planning-development',
    'water-resilience': 'water-infrastructure-resilience',
    'water-marine-environments': 'hydrographic-marine-surveying',
    'land-natural-resources': 'land-natural-resources',
    'urban-renewal': 'urban-renewal',
    'property-assets-investment': 'property-asset-valuation'
  };

  var SCENE_ALIASES = {
    digital_city: 'digital-city', digital: 'digital-city',
    'city-plus-one': 'green-city-service', environment: 'green-city-service',
    green: 'green-city-service', green_city: 'green-city-service',
    infrastructure: 'infrastructure-engineering', industry: 'industry-park-development',
    industry_park: 'industry-park-development', water: 'water-resilience',
    land: 'land-natural-resources', renewal: 'urban-renewal',
    property: 'property-assets-investment', property_assets: 'property-assets-investment'
  };

  /* 友好、清晰的 Mapbox SaaS 级微文案 */
  var SHARE_COPY = {
    en: {
      sharing: 'Preparing share link…',
      shareOpened: 'Share panel opened.',
      cancelled: 'Share cancelled.',
      copied: 'Link copied to clipboard!',
      copySuccess: 'Copied!',
      copying: 'Copying…'
    },
    'zh-Hant': {
      sharing: '正在準備分享連結…',
      shareOpened: '已開啟分享面板。',
      cancelled: '已取消分享。',
      copied: '連結已成功複製到剪貼簿！',
      copySuccess: '已複製！',
      copying: '正在複製…'
    },
    'zh-Hans': {
      sharing: '正在准备分享链接…',
      shareOpened: '已打开分享面板。',
      cancelled: '已取消分享。',
      copied: '链接已成功复制到剪贴板！',
      copySuccess: '已复制！',
      copying: '正在复制…'
    }
  };

  function q(root, selector) { return root && root.querySelector ? root.querySelector(selector) : null; }
  function qa(root, selector) { return Array.prototype.slice.call(root && root.querySelectorAll ? root.querySelectorAll(selector) : []); }
  function emit(name, detail) { try { window.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) {} }

  function getLocaleCopy() {
    var lang = (document.documentElement.getAttribute('lang') || navigator.language || 'en').toLowerCase();
    if (lang.indexOf('zh-cn') === 0 || lang.indexOf('zh-hans') === 0) return SHARE_COPY['zh-Hans'];
    if (lang.indexOf('zh') === 0) return SHARE_COPY['zh-Hant'];
    return SHARE_COPY.en;
  }

  function stableId(prefix) {
    var token = '';
    try {
      token = window.crypto && window.crypto.getRandomValues
        ? Array.prototype.map.call(window.crypto.getRandomValues(new Uint8Array(6)), function (n) { return n.toString(16).padStart(2, '0'); }).join('')
        : Math.random().toString(36).slice(2, 12);
    } catch (e) {
      token = Math.random().toString(36).slice(2, 12);
    }
    return (prefix || 'lg') + '-' + Date.now().toString(36) + '-' + token;
  }

  function normalizeScene(value, fallback) {
    var scene = String(value || fallback || '').trim().replace(/_/g, '-');
    return SCENE_ALIASES[scene] || scene || 'green-city-service';
  }

  function registryResolve(value) {
    var reg = window.GLGMarketRegistry;
    if (!reg || typeof reg.resolve !== 'function') return null;
    try { return reg.resolve(value); } catch (e) { return null; }
  }

  function marketForScene(scene, raw) {
    var direct = String(raw || '').trim();
    if (direct && direct !== 'ai-smart-solutions') return PUBLIC_HANDLE_FALLBACKS[direct] || direct;
    var resolved = registryResolve(scene);
    var candidate = (resolved && (resolved.public_market_handle || resolved.market_handle || resolved.handle)) || scene || 'green-city-service';
    return PUBLIC_HANDLE_FALLBACKS[candidate] || candidate;
  }

  function buildCityReturnUrl(payload) {
    payload = payload || {};
    var scene = normalizeScene(payload.scene || payload.booth || payload.island || payload.theme, payload.theme);
    var market = marketForScene(scene, payload.market);
    var params = new URLSearchParams();
    params.set('scene', scene);
    params.set('source', payload.source || 'share');
    params.set('intent', payload.intent || payload.missionId || 'city-mission-card');
    params.set('booth', payload.booth || scene);
    params.set('market', market);
    params.set('public_market_handle', market);
    params.set('ref', payload.ref || stableId('ref'));
    params.set('cardId', payload.cardId || stableId('c1'));
    return '/pages/game?' + params.toString();
  }

  /* 优雅的剪贴板复制，自带可感知交互 (Tactile Feedback) */
  function copyToClipboard(text, statusNode, copyBtnNode) {
    var copy = getLocaleCopy();
    if (statusNode) {
      statusNode.textContent = copy.copying;
      statusNode.removeAttribute('hidden');
    }

    function triggerSuccess() {
      if (statusNode) statusNode.textContent = copy.copied;
      if (copyBtnNode) {
        var copyIcon = q(copyBtnNode, '[data-copy-icon]');
        var successIcon = q(copyBtnNode, '[data-success-icon]');
        var copyText = q(copyBtnNode, '[data-copy-text]');

        if (copyIcon) copyIcon.hidden = true;
        if (successIcon) successIcon.hidden = false;
        if (copyText) {
          copyText.__originalText = copyText.__originalText || copyText.textContent;
          copyText.textContent = copy.copySuccess;
        }

        setTimeout(function () {
          if (copyIcon) copyIcon.hidden = false;
          if (successIcon) successIcon.hidden = true;
          if (copyText && copyText.__originalText) copyText.textContent = copyText.__originalText;
        }, 2200);
      }
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(triggerSuccess).catch(function () {
        fallbackCopy(text, triggerSuccess);
      });
    } else {
      fallbackCopy(text, triggerSuccess);
    }
  }

  function fallbackCopy(text, callback) {
    try {
      var textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      if (callback) callback();
    } catch (e) {}
  }

  /* 原生 Web Share 支持 */
  function performShare(payload, status) {
    var copy = getLocaleCopy();
    var text = payload.shareScene === 'city-plus-one'
      ? (payload.adultLine || 'I made this place +0.01% better today.') + ' Your turn.'
      : 'I completed a LittleG mission.';
    var url = new URL(payload.returnUrl || window.location.href, window.location.origin).toString();
    var shareData = { title: payload.context || 'LittleG Location', text: text, url: url };

    if (status) status.textContent = copy.sharing;

    if (navigator.share) {
      navigator.share(shareData).then(function () {
        if (status) status.textContent = copy.shareOpened;
        emit('littleg:share-ready', payload);
      }).catch(function (err) {
        if (err && err.name === 'AbortError') {
          if (status) status.textContent = copy.cancelled;
          return;
        }
        copyToClipboard(url, status, null);
        emit('littleg:share-ready', payload);
      });
      return;
    }
    copyToClipboard(url, status, null);
    emit('littleg:share-ready', payload);
  }

  /* 绑定 <share-button> 组件的弹窗控制逻辑 */
  function bindShareComponents() {
    qa(document, 'share-button, .mapbox-share').forEach(function (container) {
      if (container.__mapboxShareBound) return;
      container.__mapboxShareBound = true;

      var details = q(container, 'details');
      var input = q(container, 'input[readonly]');
      var copyBtn = q(container, '[data-share-copy], .share-button__copy');
      var closeBtn = q(container, '[data-share-close], .share-button__close');
      var statusMsg = q(container, '[role="status"]');
      var shareUrl = container.getAttribute('data-share-url') || (input ? input.value : window.location.href);

      if (copyBtn) {
        copyBtn.addEventListener('click', function (e) {
          e.preventDefault();
          var targetUrl = (input && input.value) ? input.value : shareUrl;
          copyToClipboard(targetUrl, statusMsg, copyBtn);
        });
      }

      if (closeBtn && details) {
        closeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          details.removeAttribute('open');
        });
      }

      if (input) {
        input.addEventListener('focus', function () { input.select(); });
      }
    });
  }

  function init() {
    bindShareComponents();
    document.documentElement.setAttribute('data-littleg-share-runtime', 'ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.LittleGShare = {
    version: 'mapbox-assembly-share-v2',
    copyToClipboard: copyToClipboard,
    performShare: performShare,
    buildCityReturnUrl: buildCityReturnUrl,
    bindComponents: bindShareComponents
  };
}());